import { Fragment, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  History,
  Layers,
  TrendingUp,
  Trash2,
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { parseReferenceDateKey, profitFromSnapshotOutcome, toReferenceDateKey } from '@/utils/savedMultiples';
import { toast } from 'sonner';

export default function GestaoSavedMultiplesPanel({
  snapshots,
  removeSnapshot,
  updateSnapshot = () => {},
}) {
  const [filterDate, setFilterDate] = useState(undefined);
  const [expandedId, setExpandedId] = useState(null);

  const datesWithRecords = useMemo(() => {
    const set = new Set(snapshots.map((s) => s.referenceDate));
    return Array.from(set)
      .map(parseReferenceDateKey)
      .filter(Boolean);
  }, [snapshots]);

  const filteredSnapshots = useMemo(() => {
    if (!filterDate) return snapshots;
    const key = toReferenceDateKey(filterDate);
    return snapshots.filter((s) => s.referenceDate === key);
  }, [snapshots, filterDate]);

  const chartData = useMemo(() => {
    const map = new Map();
    for (const s of snapshots) {
      const k = s.referenceDate;
      if (!map.has(k)) {
        map.set(k, {
          dateKey: k,
          label: k,
          stake: 0,
          profitRecorded: 0,
          greens: 0,
          reds: 0,
          pending: 0,
          saves: 0,
        });
      }
      const row = map.get(k);
      row.stake += Number(s.stake) || 0;
      row.saves += 1;
      if (s.outcome === 'green') {
        row.greens += 1;
        row.profitRecorded += profitFromSnapshotOutcome(s);
      } else if (s.outcome === 'red') {
        row.reds += 1;
        row.profitRecorded += profitFromSnapshotOutcome(s);
      } else {
        row.pending += 1;
      }
    }
    return Array.from(map.values())
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
      .map((row) => ({
        ...row,
        label: (() => {
          try {
            return format(parseISO(`${row.dateKey}T12:00:00`), 'dd/MM', { locale: ptBR });
          } catch {
            return row.dateKey;
          }
        })(),
      }));
  }, [snapshots]);

  const cumulativeData = useMemo(() => {
    let acc = 0;
    return chartData.map((row) => {
      acc += row.profitRecorded;
      return { ...row, cumulative: Math.round(acc * 100) / 100 };
    });
  }, [chartData]);

  const outcomeSummary = useMemo(() => {
    let greens = 0;
    let reds = 0;
    let pending = 0;
    let profitTotal = 0;
    for (const s of snapshots) {
      if (s.outcome === 'green') {
        greens += 1;
        profitTotal += profitFromSnapshotOutcome(s);
      } else if (s.outcome === 'red') {
        reds += 1;
        profitTotal += profitFromSnapshotOutcome(s);
      } else {
        pending += 1;
      }
    }
    return { greens, reds, pending, profitTotal };
  }, [snapshots]);

  return (
    <Card className="mt-8 overflow-hidden rounded-3xl border border-slate-200/70 dark:border-white/[0.06] bg-white/80 dark:bg-neutral-900/60 backdrop-blur-2xl shadow-[0_8px_32px_-6px_rgba(15,23,42,0.10)] dark:shadow-[0_8px_48px_-10px_rgba(0,0,0,0.6)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent dark:via-emerald-400/30" />
      <CardHeader className="relative space-y-1 border-b border-slate-200/60 pb-4 dark:border-white/[0.06]">
        <CardTitle className="flex items-center gap-2.5 text-lg font-bold text-slate-900 dark:text-white">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25">
            <History className="h-4 w-4 text-white" />
          </div>
          Histórico e análise
        </CardTitle>
        <CardDescription className="text-sm leading-relaxed text-slate-600 dark:text-neutral-400">
          Marque cada registo como Green ou Red; o gráfico soma o lucro real por data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 pt-6">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-gray-100">
                <CalendarDays className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                Filtrar por dia
              </p>
              {filterDate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setFilterDate(undefined)}
                >
                  Ver todos
                </Button>
              )}
            </div>
            <Calendar
              mode="single"
              selected={filterDate}
              onSelect={setFilterDate}
              locale={ptBR}
              weekStartsOn={0}
              modifiers={{ temRegisto: datesWithRecords }}
              modifiersClassNames={{
                temRegisto:
                  'relative after:absolute after:bottom-0.5 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-emerald-500',
              }}
              className="mx-auto w-fit rounded-2xl border border-slate-200/60 bg-white p-2 dark:border-white/[0.08] dark:bg-neutral-950/50"
            />
          </div>

          <div className="min-w-0">
            <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-gray-100">
              <BarChart3 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              Lucro por data (Green / Red)
            </p>
            {snapshots.length > 0 && (
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-xl border border-emerald-200/60 bg-emerald-500/[0.07] px-3 py-2.5 dark:border-emerald-700/30 dark:bg-emerald-500/10">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-600/80 dark:text-emerald-400/70">Green</p>
                  <p className="mt-0.5 text-xl font-black tabular-nums text-emerald-700 dark:text-emerald-300">{outcomeSummary.greens}</p>
                </div>
                <div className="rounded-xl border border-rose-200/60 bg-rose-500/[0.07] px-3 py-2.5 dark:border-rose-700/30 dark:bg-rose-500/10">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-rose-600/80 dark:text-rose-400/70">Red</p>
                  <p className="mt-0.5 text-xl font-black tabular-nums text-rose-700 dark:text-rose-300">{outcomeSummary.reds}</p>
                </div>
                <div className="rounded-xl border border-slate-200/70 bg-slate-100/60 px-3 py-2.5 dark:border-white/[0.06] dark:bg-neutral-800/40">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500">Pendente</p>
                  <p className="mt-0.5 text-xl font-black tabular-nums text-slate-700 dark:text-neutral-300">{outcomeSummary.pending}</p>
                </div>
                <div className={cn(
                  'rounded-xl border px-3 py-2.5',
                  outcomeSummary.profitTotal >= 0
                    ? 'border-emerald-300/50 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 dark:border-emerald-600/30 dark:from-emerald-500/15 dark:to-teal-500/5'
                    : 'border-rose-300/50 bg-gradient-to-br from-rose-500/10 to-pink-500/5 dark:border-rose-600/30 dark:from-rose-500/15 dark:to-pink-500/5',
                )}>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500">Total</p>
                  <p className={cn(
                    'mt-0.5 text-xl font-black tabular-nums',
                    outcomeSummary.profitTotal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                  )}>
                    R$ {outcomeSummary.profitTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}
            {chartData.length === 0 ? (
              <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-slate-200/80 text-sm text-slate-500 dark:border-white/[0.08] dark:text-neutral-500">
                Guarde uma múltipla para ver o gráfico.
              </div>
            ) : (
              <div className="space-y-5">
                <div className="h-56 w-full min-w-0 rounded-2xl border border-slate-200/50 bg-gradient-to-b from-white to-slate-50/50 p-3 dark:border-white/[0.06] dark:from-neutral-950/60 dark:to-neutral-900/30">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${v}`} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 14,
                          border: '1px solid rgba(148,163,184,0.2)',
                          background: 'rgba(15,23,42,0.92)',
                          color: '#e2e8f0',
                          fontSize: 12,
                          boxShadow: '0 8px 32px -4px rgba(0,0,0,0.4)',
                          backdropFilter: 'blur(12px)',
                        }}
                        formatter={(value, name) => [
                          typeof value === 'number'
                            ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : value,
                          name === 'stake' ? 'Stake (soma)' : name === 'profitRecorded' ? 'Lucro Green/Red' : name,
                        ]}
                        labelFormatter={(_, payload) => {
                          const p = payload?.[0]?.payload;
                          if (!p?.dateKey) return '';
                          const d = format(parseISO(`${p.dateKey}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR });
                          return `${d} · ${p.greens}G ${p.reds}R${p.pending ? ` · ${p.pending} pend.` : ''}`;
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="stake" name="Stake (soma)" fill="#475569" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="profitRecorded" name="Lucro Green/Red" radius={[6, 6, 0, 0]}>
                        {chartData.map((entry, i) => (
                          <Cell
                            key={`pr-${entry.dateKey}-${i}`}
                            fill={entry.profitRecorded >= 0 ? '#10b981' : '#f43f5e'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {outcomeSummary.greens === 0 && outcomeSummary.reds === 0 && snapshots.length > 0 && (
                    <p className="mt-2 text-center text-[11px] text-amber-700 dark:text-amber-400/90">
                      Marque Green ou Red na tabela abaixo para preencher as barras de lucro.
                    </p>
                  )}
                </div>

                <div>
                  <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-gray-100">
                    <TrendingUp className="h-4 w-4 text-emerald-400" />
                    Lucro acumulado
                  </p>
                  <div className="relative h-56 w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200/50 bg-gradient-to-b from-slate-950 to-slate-900 p-3 dark:border-white/[0.06]">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(57,255,20,0.06),transparent_70%)]" />
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cumulativeData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                        <defs>
                          <linearGradient id="neonGreenGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#39FF14" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#39FF14" stopOpacity={0.02} />
                          </linearGradient>
                          <filter id="neonGlow">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feMerge>
                              <feMergeNode in="blur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${v}`} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 14,
                            border: '1px solid rgba(57,255,20,0.2)',
                            background: 'rgba(15,23,42,0.95)',
                            color: '#e2e8f0',
                            fontSize: 12,
                            boxShadow: '0 0 24px -4px rgba(57,255,20,0.15), 0 8px 32px -4px rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(12px)',
                          }}
                          formatter={(value) => [
                            typeof value === 'number'
                              ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : value,
                            'Acumulado',
                          ]}
                          labelFormatter={(_, payload) => {
                            const p = payload?.[0]?.payload;
                            if (!p?.dateKey) return '';
                            try {
                              return format(parseISO(`${p.dateKey}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR });
                            } catch {
                              return p.dateKey;
                            }
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="cumulative"
                          stroke="#39FF14"
                          strokeWidth={2.5}
                          fill="url(#neonGreenGrad)"
                          filter="url(#neonGlow)"
                          dot={{ r: 4, fill: '#0f172a', stroke: '#39FF14', strokeWidth: 2 }}
                          activeDot={{ r: 6, fill: '#39FF14', stroke: '#0f172a', strokeWidth: 2, filter: 'url(#neonGlow)' }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-gray-100">
            <Layers className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            Tabela de registros
            {filterDate && (
              <Badge variant="outline" className="ml-1 text-[10px] font-semibold tabular-nums">
                {format(filterDate, 'dd/MM/yyyy', { locale: ptBR })}
              </Badge>
            )}
          </p>
          {filteredSnapshots.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200/80 px-4 py-10 text-center text-sm text-slate-500 dark:border-white/[0.08] dark:text-neutral-500">
              {snapshots.length === 0
                ? 'Nenhum registro salvo ainda.'
                : 'Nenhum registro para o dia selecionado.'}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200/60 dark:border-white/[0.06] shadow-sm dark:shadow-black/20">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b border-slate-200/80 bg-gradient-to-b from-slate-50 to-slate-100/80 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:border-white/[0.06] dark:from-neutral-900/80 dark:to-neutral-950/60 dark:text-neutral-400">
                  <tr>
                    <th className="px-3 py-3">Data</th>
                    <th className="px-3 py-3">Título</th>
                    <th className="px-3 py-3 text-center">Sel.</th>
                    <th className="px-3 py-3 text-right">Odd</th>
                    <th className="px-3 py-3 text-right">Stake</th>
                    <th className="px-3 py-3 text-right">Retorno</th>
                    <th className="px-3 py-3 text-right">Lucro est.</th>
                    <th className="px-3 py-3 text-center min-w-[9rem]">Resultado</th>
                    <th className="px-3 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/[0.06]">
                  {filteredSnapshots.map((s) => {
                    const open = expandedId === s.id;
                    return (
                      <Fragment key={s.id}>
                        <tr className="bg-white/90 dark:bg-neutral-950/30 hover:bg-slate-50/80 dark:hover:bg-neutral-900/40">
                          <td className="px-3 py-2.5 tabular-nums text-slate-700 dark:text-neutral-300">
                            {(() => {
                              try {
                                return format(parseISO(`${s.referenceDate}T12:00:00`), 'dd/MM/yyyy', {
                                  locale: ptBR,
                                });
                              } catch {
                                return s.referenceDate;
                              }
                            })()}
                          </td>
                          <td className="max-w-[200px] truncate px-3 py-2.5 font-medium text-slate-900 dark:text-gray-100">
                            {s.title}
                          </td>
                          <td className="px-3 py-2.5 text-center tabular-nums text-slate-600 dark:text-neutral-400">
                            {s.legsCount ?? s.legs?.length ?? 0}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-800 dark:text-gray-100">
                            {s.combinedOdd != null ? Number(s.combinedOdd).toFixed(2) : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-neutral-300">
                            R${' '}
                            {Number(s.stake || 0).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                            {s.grossReturn != null
                              ? `R$ ${Number(s.grossReturn).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                              : '—'}
                          </td>
                          <td
                            className={cn(
                              'px-3 py-2.5 text-right tabular-nums font-semibold',
                              s.estimatedProfit != null && Number(s.estimatedProfit) >= 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-600 dark:text-rose-400',
                            )}
                          >
                            {s.estimatedProfit != null
                              ? `R$ ${Number(s.estimatedProfit).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                              : '—'}
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex flex-col items-stretch gap-1 sm:flex-row sm:items-center sm:justify-center">
                              <div className="flex gap-1 justify-center">
                                <Button
                                  type="button"
                                  size="sm"
                                  className={cn(
                                    'h-8 flex-1 px-2 text-[10px] font-bold uppercase sm:flex-none',
                                    s.outcome === 'green'
                                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                      : 'border border-emerald-300/70 bg-emerald-500/5 text-emerald-800 hover:bg-emerald-500/15 dark:border-emerald-700 dark:text-emerald-300',
                                  )}
                                  variant={s.outcome === 'green' ? 'default' : 'outline'}
                                  onClick={() => updateSnapshot(s.id, { outcome: 'green' })}
                                >
                                  Green
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  className={cn(
                                    'h-8 flex-1 px-2 text-[10px] font-bold uppercase sm:flex-none',
                                    s.outcome === 'red'
                                      ? 'bg-rose-600 text-white hover:bg-rose-700'
                                      : 'border border-rose-300/70 bg-rose-500/5 text-rose-800 hover:bg-rose-500/15 dark:border-rose-800 dark:text-rose-300',
                                  )}
                                  variant={s.outcome === 'red' ? 'default' : 'outline'}
                                  onClick={() => updateSnapshot(s.id, { outcome: 'red' })}
                                >
                                  Red
                                </Button>
                              </div>
                              {(s.outcome === 'green' || s.outcome === 'red') && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-slate-500 hover:text-slate-800 dark:text-neutral-500 dark:hover:text-neutral-200"
                                  onClick={() => updateSnapshot(s.id, { outcome: null })}
                                >
                                  Pendente
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="px-1 py-2.5">
                            <div className="flex items-center gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                aria-label={open ? 'Fechar detalhes' : 'Ver seleções'}
                                onClick={() => setExpandedId(open ? null : s.id)}
                              >
                                {open ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 text-slate-400 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                                aria-label="Apagar registro"
                                onClick={() => {
                                  removeSnapshot(s.id);
                                  setExpandedId((id) => (id === s.id ? null : id));
                                  toast.message('Registro removido');
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-slate-50/95 dark:bg-neutral-900/50">
                            <td colSpan={9} className="px-3 py-3 text-xs text-slate-600 dark:text-neutral-400">
                              <p className="mb-2 font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-500">
                                Seleções salvas
                              </p>
                              <ul className="space-y-1.5">
                                {(s.legs || []).map((leg) => (
                                  <li
                                    key={leg.id}
                                    className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200/60 pb-1.5 last:border-0 dark:border-white/[0.06]"
                                  >
                                    <span className="min-w-0 flex-1 text-slate-800 dark:text-gray-200">
                                      {leg.label}
                                    </span>
                                    <span className="shrink-0 tabular-nums font-semibold text-slate-700 dark:text-neutral-300">
                                      {leg.decimalOdd != null ? Number(leg.decimalOdd).toFixed(2) : '—'}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
